import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Paper, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { cloneDeep, findIndex } from 'lodash-es';
import { useStore } from '@mcn-platform/models';
import Header from './Header';
import Note from './Note';
import AddNote from './AddNote';
const ConfirmDelete = ({ note, open, deleteNote, cancelAction, }) => (_jsxs(Dialog, { open: open, onClose: cancelAction, "aria-labelledby": "alert-dialog-title", "aria-describedby": "alert-dialog-description", children: [_jsx(DialogTitle, { id: "alert-dialog-title", children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(DialogContent, { id: "alert-dialog-description", children: ["Are you sure you want to delete the note?", _jsx(Box, { children: note && note.name })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => deleteNote(note), children: "Delete" }), _jsx(Button, { onClick: cancelAction, autoFocus: true, children: "Cancel" })] })] }));
const Notes = ({ makeReference, isCourseItemSelected, handleViewNote, settings, progress, modelName, }) => {
    const store = useStore();
    const notes = progress && progress.notes && progress.notes.items
        ? progress.notes.items : [];
    const [activeNote, setActiveNote] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const handleDeleteNote = (note) => {
        console.log('Delete requested', note);
        setActiveNote(note);
        setDeleteOpen(true);
    };
    const retryOperation = async (operation, operationName, retries = 3, delay = 100) => {
        try {
            await operation();
        }
        catch (error) {
            if (retries > 0) {
                console.log(`Error ${operationName}, retrying... (${retries} attempts left)`);
                await new Promise((resolve) => { setTimeout(resolve, delay); });
                await retryOperation(operation, operationName, retries - 1);
            }
            else {
                console.error(`Failed to ${operationName} after multiple retries:`, error);
                throw error;
            }
        }
    };
    const getProgress = () => progress;
    const deleteNote = (note) => {
        console.log('Delete confirmed', note);
        setDeleteOpen(false);
        setActiveNote(null);
        const deleteOperation = async () => {
            const latestProgress = getProgress();
            const notesClone = cloneDeep(latestProgress.notes);
            const index = findIndex(notesClone.items, { id: note.id });
            notesClone.items.splice(index, 1);
            latestProgress.updateValues({ notes: notesClone });
            const { success, reason } = await store.update(modelName, latestProgress);
            if (!success) {
                if (reason === 'concurrency') {
                    // The concurrency error message was already shown by RootModel.
                    // Optionally, refresh UI or notify user.
                    return;
                }
                throw Error('Failed to delete note');
            }
        };
        retryOperation(deleteOperation, 'delete note');
    };
    const updateNote = (note, values) => {
        const updateOperation = async () => {
            const latestProgress = getProgress();
            const notesClone = cloneDeep(latestProgress.notes);
            const index = findIndex(notesClone.items, { id: note.id });
            notesClone.items[index] = {
                ...note,
                ...values,
            };
            latestProgress.updateValues({ notes: notesClone });
            const { success, reason } = await store.update(modelName, latestProgress);
            if (!success) {
                if (reason === 'concurrency') {
                    // The concurrency error message was already shown by RootModel.
                    // Optionally, refresh UI or notify user.
                    return;
                }
                throw Error('Failed to update note');
            }
        };
        retryOperation(updateOperation, 'update note');
    };
    const handleAddNote = (note) => {
        const addOperation = async () => {
            const latestProgress = getProgress();
            const notesClone = cloneDeep(latestProgress.notes || []);
            if (notesClone.items) {
                notesClone.items.push(note);
            }
            else {
                notesClone.items = [note];
            }
            latestProgress.updateValues({ notes: notesClone });
            const { success, reason } = await store.update(modelName, latestProgress);
            if (!success) {
                if (reason === 'concurrency') {
                    // The concurrency error message was already shown by RootModel.
                    // Optionally, refresh UI or notify user.
                    return;
                }
                throw Error('Failed to add note');
            }
        };
        retryOperation(addOperation, 'add note');
    };
    return (_jsxs(Paper, { sx: { mt: 2 }, children: [settings.showHeader && (_jsx(Header, { onDownload: () => console.log('download note'), onPost: () => console.log('submit note'), onPrint: () => console.log('print note'), settings: {
                    ...settings,
                    // force them off for now
                    showHeaderActions: false,
                    showNotesDownloadBtn: false,
                    showNotesPrintBtn: false,
                    showNotesShareBtn: false,
                } })), _jsx(Grid, { container: true, spacing: 1, children: notes.map((note) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(Note, { note: note, requestView: handleViewNote, requestDelete: handleDeleteNote, requestSave: updateNote }) }, note.id))) }), _jsx(AddNote, { makeReference: makeReference, isCourseItemSelected: isCourseItemSelected, handleAddNote: handleAddNote }), _jsx(ConfirmDelete, { note: activeNote, open: deleteOpen, deleteNote: deleteNote, cancelAction: () => setDeleteOpen(false) })] }));
};
Notes.defaultProps = {
    settings: {
        showHeader: true,
        showHeaderTitle: true,
        showHeaderActions: true,
        showNotesDownloadBtn: true,
        showNotesShareBtn: true,
        showNotesPrintBtn: true,
    },
};
Notes.meta = {
    group: 'hidden',
    name: 'Course Notes Panel',
    description: 'Component displays the course notes.',
    props: [
        {
            key: 'course',
            name: 'Course',
            type: 'course', // note this is a special type recognized by the system
            description: 'The course to display information about',
        },
        {
            key: 'settings.showHeader',
            name: 'Show header',
            type: 'boolean',
            description: 'Show header.',
        },
        {
            key: 'settings.showHeaderTitle',
            name: 'Show header title',
            type: 'boolean',
            description: 'Show header title.',
        },
        {
            key: 'settings.showHeaderActions',
            name: 'Show header actions',
            type: 'boolean',
            description: 'Show header actions.',
        },
        {
            key: 'settings.showNotesDownloadBtn',
            name: 'Show notes download button',
            type: 'boolean',
            description: 'Show notes download button.',
        },
        {
            key: 'settings.showNotesShareBtn',
            name: 'Show notes share button',
            type: 'boolean',
            description: 'Show notes share button.',
        },
        {
            key: 'settings.showNotesPrintBtn',
            name: 'Show notes print button',
            type: 'boolean',
            description: 'Show notes print button.',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'Notes',
        props: {
            settings: {
                showHeader: true,
                showHeaderTitle: true,
                showHeaderActions: true,
                showNotesDownloadBtn: true,
                showNotesShareBtn: true,
                showNotesPrintBtn: true,
            },
        },
    },
};
export default observer(Notes);
//# sourceMappingURL=Notes.js.map